import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { releaseApi } from '../api'
import type { ImageSummary, ImageWorkItem } from '../api'

function formatDate(iso: string): string {
    return new Date(iso).toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
    })
}

function workItemTypeBadge(type: string) {
    const cls = type === 'Bug'
        ? 'bg-red-500/15 text-red-300 border border-red-500/30'
        : 'bg-indigo-500/15 text-indigo-300 border border-indigo-500/30'
    return <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-medium ${cls}`}>{type}</span>
}

function stateBadge(state: string) {
    const cls = state === 'Resolved'
        ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30'
        : state === 'Active'
        ? 'bg-amber-500/15 text-amber-300 border border-amber-500/30'
        : 'bg-slate-700/50 text-slate-400 border border-slate-700'
    return <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-medium ${cls}`}>{state}</span>
}

function WorkItemRow({ item }: { item: ImageWorkItem }) {
    return (
        <div className="flex items-start gap-3 py-1.5">
            <span className="font-mono text-xs text-slate-500 shrink-0 w-10">#{item.id}</span>
            <span className="flex-1 text-sm text-slate-300 min-w-0 break-words">{item.title}</span>
            <div className="flex items-center gap-1.5 shrink-0">
                {workItemTypeBadge(item.workItemType)}
                {stateBadge(item.state)}
            </div>
        </div>
    )
}

function ImageRow({ image, searchLower }: { image: ImageSummary; searchLower: string }) {
    const [expanded, setExpanded] = useState(false)

    const matchingItems = useMemo(() => {
        if (!searchLower) return image.workItems
        return image.workItems.filter(wi =>
            wi.title.toLowerCase().includes(searchLower) ||
            String(wi.id).includes(searchLower)
        )
    }, [image.workItems, searchLower])

    // If searching and no work items match and image ref doesn't match, hide row.
    const imageRefMatches = !searchLower || image.imageRef.includes(searchLower)
    if (searchLower && matchingItems.length === 0 && !imageRefMatches) return null

    const displayItems = searchLower ? matchingItems : image.workItems

    return (
        <div className="border border-slate-800 rounded-xl bg-slate-900/40 overflow-hidden">
            <button
                className="w-full text-left px-5 py-4 flex items-center gap-4 hover:bg-slate-800/30 transition-colors"
                onClick={() => setExpanded(e => !e)}
            >
                <svg
                    className={`h-3.5 w-3.5 shrink-0 text-slate-500 transition-transform ${expanded ? 'rotate-90' : ''}`}
                    viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"
                >
                    <path d="M6 4l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>

                <code className="text-sm font-mono text-emerald-400 shrink-0">{image.imageRef}</code>

                <div className="flex-1 min-w-0 flex items-center gap-3">
                    <span className="text-xs text-slate-500 font-mono truncate">
                        {image.sourceVersion.slice(0, 12)}
                    </span>
                    {image.pipelines.map(p => (
                        <span key={p} className="text-[10px] text-slate-500 bg-slate-800 border border-slate-700 rounded px-1.5 py-0.5 truncate max-w-[180px]">{p}</span>
                    ))}
                </div>

                <div className="flex items-center gap-4 shrink-0">
                    <span className="text-xs text-slate-500">{formatDate(image.buildDate)}</span>
                    <span className="text-xs text-slate-500 tabular-nums">
                        {image.workItems.length === 0 ? (
                            <span className="text-slate-600">no items</span>
                        ) : (
                            `${image.workItems.length} item${image.workItems.length === 1 ? '' : 's'}`
                        )}
                    </span>
                </div>
            </button>

            {expanded && (
                <div className="border-t border-slate-800 px-5 py-3">
                    {displayItems.length === 0 ? (
                        <p className="text-sm text-slate-600 py-1">No work items linked to this image.</p>
                    ) : (
                        <div className="divide-y divide-slate-800/60">
                            {displayItems.map(wi => <WorkItemRow key={wi.id} item={wi} />)}
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export function ImagesPage() {
    const [search, setSearch] = useState('')
    const [sortBy, setSortBy] = useState<'date' | 'items'>('date')
    const [filterState, setFilterState] = useState<'all' | 'resolved' | 'active'>('all')

    const { data, isLoading, error } = useQuery({
        queryKey: ['images'],
        queryFn: releaseApi.getImages,
        staleTime: 60_000,
        refetchInterval: 120_000,
    })

    const searchLower = search.trim().toLowerCase()

    const sorted = useMemo(() => {
        if (!data) return []
        let imgs = [...data.images]

        if (filterState !== 'all') {
            imgs = imgs.filter(img =>
                img.workItems.some(wi => wi.state.toLowerCase() === filterState)
            )
        }

        if (sortBy === 'items') {
            imgs.sort((a, b) => b.workItems.length - a.workItems.length)
        } else {
            imgs.sort((a, b) => new Date(b.buildDate).getTime() - new Date(a.buildDate).getTime())
        }

        return imgs
    }, [data, sortBy, filterState])

    return (
        <div className="flex flex-col h-full">
            <div className="border-b border-slate-800 px-4 sm:px-6 py-4">
                <div className="flex items-center justify-between gap-4">
                    <div>
                        <h1 className="text-lg font-semibold text-white">Images</h1>
                        <p className="text-xs text-slate-500 mt-0.5">Build images and their associated work items</p>
                    </div>

                    <div className="flex items-center gap-3">
                        {/* State filter */}
                        <select
                            value={filterState}
                            onChange={e => setFilterState(e.target.value as typeof filterState)}
                            className="rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-300 focus:outline-none focus:ring-1 focus:ring-slate-600"
                        >
                            <option value="all">All states</option>
                            <option value="resolved">Resolved</option>
                            <option value="active">Active</option>
                        </select>

                        {/* Sort */}
                        <select
                            value={sortBy}
                            onChange={e => setSortBy(e.target.value as typeof sortBy)}
                            className="rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-300 focus:outline-none focus:ring-1 focus:ring-slate-600"
                        >
                            <option value="date">Sort: Date</option>
                            <option value="items">Sort: Item count</option>
                        </select>

                        {/* Search */}
                        <div className="relative">
                            <svg
                                className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500 pointer-events-none"
                                viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"
                            >
                                <circle cx="6.5" cy="6.5" r="4.5" />
                                <path d="M10 10l3.5 3.5" strokeLinecap="round" />
                            </svg>
                            <input
                                type="text"
                                placeholder="Search image, title, id…"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                className="rounded-lg border border-slate-700 bg-slate-800 pl-8 pr-3 py-1.5 text-sm text-slate-300 placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-slate-600 w-56"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">
                {isLoading && (
                    <div className="flex items-center justify-center py-16 text-sm text-slate-500">Loading images…</div>
                )}

                {error && (
                    <div className="rounded-xl border border-red-800/40 bg-red-900/10 px-5 py-4 text-sm text-red-300">
                        Failed to load images.
                    </div>
                )}

                {!isLoading && !error && sorted.length === 0 && (
                    <div className="flex items-center justify-center py-16 text-sm text-slate-600">
                        {searchLower ? 'No images match your search.' : 'No images found.'}
                    </div>
                )}

                {!isLoading && !error && sorted.length > 0 && (
                    <div className="flex flex-col gap-2">
                        {sorted.map(img => (
                            <ImageRow key={img.imageRef} image={img} searchLower={searchLower} />
                        ))}
                    </div>
                )}
            </div>

            {data && (
                <div className="border-t border-slate-800 px-4 sm:px-6 py-2 text-[10px] text-slate-600">
                    {sorted.length} image{sorted.length === 1 ? '' : 's'} · fetched {formatDate(data.fetchedAt)}
                </div>
            )}
        </div>
    )
}
